import { CSSProperties, PropsWithChildren, forwardRef } from 'react';

import { Tooltip as MuiTooltip, TooltipProps as MuiTooltipProps } from '@mui/material';
import { Tooltip as DsTooltip } from '@natter-co/design-system';
import { VISUAL_REFRESH } from 'common/utils';

import { toDsProps } from './toDsProps';

export type TooltipProps = MuiTooltipProps & {
  variant?: 'light' | 'dark';
  display?: CSSProperties['display'];
  /**
   * Hand the child straight to MUI instead of wrapping it in the ref-safety `<span>`. Only for
   * children that already forward their ref and spread their props — a `styled()` DOM element does.
   *
   * Needed whenever the child itself must receive MUI's ARIA wiring: `describeChild` clones
   * `aria-describedby` onto MUI's *direct* child, so with the wrapper in place the description binds
   * to the span and the real control never advertises it. Hover and focus still open the tooltip, so
   * the omission is invisible until someone uses a screen reader.
   *
   * Makes `display` a no-op: there is no span left to lay out.
   */
  disableWrapper?: boolean;
  /** Typed because hyphenated JSX attributes are unchecked; `toDsProps` has to see it. */
  'data-testid'?: string;
};

/**
 * Handle MUI forwardRef issue https://mui.com/material-ui/guides/composition/#caveat-with-refs
 *
 * The tooltip needs to apply DOM event listeners to its child element.
 * If the child is a custom React element, you need to make sure that it spreads its props to the underlying DOM element.
 */
const TooltipContent = forwardRef<HTMLSpanElement, PropsWithChildren<Pick<TooltipProps, 'display'>>>(
  ({ display, ...props }, ref) => (
    <span {...props} ref={ref} style={{ display }}>
      {props.children}
    </span>
  )
);

export const Tooltip = (props: TooltipProps) => {
  const { children, variant = 'light', display, disableWrapper, 'data-testid': testId, ...muiProps } = props;

  // The visual-refresh gate lives here, not at the call sites. `null` falls through to MUI.
  const dsProps = VISUAL_REFRESH ? toDsProps(props) : null;

  if (dsProps) {
    // The span stays as the trigger, as it is under MUI, so layout and the ref-safety it gives
    // arbitrary children do not change. `data-testid` stays on it too, where MUI puts it, and so does
    // the title as `aria-label`: MUI has always put it there, and e2e locators find the control by it
    // (the series name editor is `getByRole('button', { name: 'Rename' })`). Dropping it silently
    // broke those on the first CI run of this change.
    const trigger = disableWrapper ? (
      children
    ) : (
      <TooltipContent display={display} data-testid={testId} aria-label={dsProps.label || undefined}>
        {children}
      </TooltipContent>
    );

    return dsProps.label ? <DsTooltip {...dsProps}>{trigger}</DsTooltip> : trigger;
  }

  return (
    <MuiTooltip classes={{ tooltip: variant }} data-testid={testId} {...muiProps}>
      {disableWrapper ? children : <TooltipContent display={display}>{children}</TooltipContent>}
    </MuiTooltip>
  );
};
